import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  FaUsers,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSearch,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import RoleIllustration from "../../components/RoleIllustration";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { getProfileImageUrl } from "../../utils/imageUtils";
import { usePopup } from "../../context/PopupContext";
import { useTranslation } from "react-i18next";
import { toLocalizedNumber } from "../../utils/localeUtils";
import Pagination from "../../components/Pagination";
import "./AdminUserManagement.css";

const PER_PAGE = 10;

const AdminUserManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast, showConfirm } = usePopup();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);

  // Determine user type from URL
  const isFarmersPage = location.pathname.includes("/farmers");
  const isConsumersPage = location.pathname.includes("/consumers");
  const currentRole = isFarmersPage
    ? "farmer"
    : isConsumersPage
      ? "consumer"
      : "all";

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchUsers();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      // Always filter by role based on current page
      if (currentRole !== "all") {
        params.append("role", currentRole);
      }
      if (filterStatus !== "all") {
        params.append("verified", filterStatus === "active" ? "true" : "false");
      }

      const response = await api.get(`/admin/users?${params.toString()}`);
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRole, filterStatus, user]);

  const handleVerify = async (userId) => {
    showConfirm({
      title: t("adminUserManagement.verifyTitle"),
      message: t("adminUserManagement.verifyMessage"),
      confirmLabel: t("adminUserManagement.verify"),
      onConfirm: async () => {
        try {
          await api.put(`/users/${userId}/verify`);
          fetchUsers();
          showToast(t("adminUserManagement.verifySuccess"), "success");
        } catch (error) {
          console.error("Error verifying user:", error);
          showToast(t("adminUserManagement.verifyFailed"), "error");
        }
      },
    });
  };

  const handleBlock = async (userId) => {
    showConfirm({
      title: t("adminUserManagement.blockTitle"),
      message: t("adminUserManagement.blockMessage"),
      confirmLabel: t("adminUserManagement.block"),
      onConfirm: async () => {
        try {
          setUsers(
            users.map((u) =>
              u._id === userId ? { ...u, verified: false } : u,
            ),
          );
          showToast(t("adminUserManagement.blockSuccess"), "success");
        } catch (error) {
          console.error("Error blocking user:", error);
          showToast(t("adminUserManagement.blockFailed"), "error");
        }
      },
    });
  };

  const handleDelete = async (userId) => {
    showConfirm({
      title: t("adminUserManagement.deleteTitle"),
      message: t("adminUserManagement.deleteMessage"),
      confirmLabel: t("adminUserManagement.delete"),
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${userId}`);
          fetchUsers();
          showToast(t("adminUserManagement.deleteSuccess"), "success");
        } catch (error) {
          console.error("Error deleting user:", error);
          showToast(t("adminUserManagement.deleteFailed"), "error");
        }
      },
    });
  };

  const filteredUsers = users.filter((userItem) => {
    // Role is already filtered by API, but double-check
    const matchesRole = currentRole === "all" || userItem.role === currentRole;
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" && userItem.verified) ||
      (filterStatus === "pending" &&
        !userItem.verified &&
        userItem.role === "farmer");
    const matchesSearch =
      userItem.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userItem.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      userItem.phone?.includes(searchQuery);
    return matchesRole && matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredUsers.length / PER_PAGE);
  const pageUsers = filteredUsers.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterStatus]);

  const getProfileImage = (userItem) => {
    if (userItem?.profileImage) {
      if (userItem.profileImage.startsWith("http")) {
        return userItem.profileImage;
      }
      return getProfileImageUrl(userItem.profileImage);
    }
    return "/images/deafultpic.png";
  };

  const stats = {
    total: users.length,
    farmers: isFarmersPage
      ? users.length
      : users.filter((u) => u.role === "farmer").length,
    consumers: isConsumersPage
      ? users.length
      : users.filter((u) => u.role === "consumer").length,
    pending: users.filter((u) => !u.verified && u.role === "farmer").length,
    active: users.filter((u) => u.verified).length,
  };

  return (
    <AdminLayout>
      <div className="admin-user-management-content">
        {/* Page Header */}
        <div className="admin-user-management-page-header">
          <div>
            <h1 className="admin-user-management-page-title">
              {isFarmersPage
                ? t("adminUserManagement.titleFarmers")
                : isConsumersPage
                  ? t("adminUserManagement.titleConsumers")
                  : t("adminUserManagement.titleAll")}
            </h1>
            <p className="admin-user-management-page-subtitle">
              {isFarmersPage
                ? t("adminUserManagement.subtitleFarmers")
                : isConsumersPage
                  ? t("adminUserManagement.subtitleConsumers")
                  : t("adminUserManagement.subtitleAll")}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-user-management-stats-cards-grid">
          {!isFarmersPage && !isConsumersPage && (
            <>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <FaUsers aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.total)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.totalUsers")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <RoleIllustration role="farmer" size="lg" />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.farmers)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.farmers")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <RoleIllustration role="consumer" size="lg" />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.consumers)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.consumers")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card warning">
                <div className="admin-user-management-stat-icon">
                  <FaExclamationTriangle aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.pending)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.pendingVerifications")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <FaCheckCircle aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.active)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.activeUsers")}
                  </div>
                </div>
              </div>
            </>
          )}
          {isFarmersPage && (
            <>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <RoleIllustration role="farmer" size="lg" />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.farmers)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.totalFarmers")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card warning">
                <div className="admin-user-management-stat-icon">
                  <FaExclamationTriangle aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.pending)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.pendingVerifications")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <FaCheckCircle aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.active)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.verifiedFarmers")}
                  </div>
                </div>
              </div>
            </>
          )}
          {isConsumersPage && (
            <>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <RoleIllustration role="consumer" size="lg" />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.consumers)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.totalConsumers")}
                  </div>
                </div>
              </div>
              <div className="admin-user-management-stat-card">
                <div className="admin-user-management-stat-icon">
                  <FaCheckCircle aria-hidden />
                </div>
                <div className="admin-user-management-stat-content">
                  <div className="admin-user-management-stat-value">
                    {toLocalizedNumber(stats.active)}
                  </div>
                  <div className="admin-user-management-stat-label">
                    {t("adminUserManagement.activeConsumers")}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filters Section */}
        <div className="admin-user-management-filters-card">
          <div className="admin-user-management-search-box">
            <input
              type="text"
              placeholder={t("adminUserManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-user-management-search-input"
            />
            <FaSearch
              className="admin-user-management-search-icon"
              aria-hidden
            />
          </div>
          <div className="admin-user-management-filter-group">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-user-management-filter-select"
            >
              <option value="all">{t("adminUserManagement.allStatus")}</option>
              <option value="active">{t("adminUserManagement.active")}</option>
              {isFarmersPage && (
                <option value="pending">
                  {t("adminUserManagement.pendingVerification")}
                </option>
              )}
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="admin-user-management-table-card">
          {loading ? (
            <div className="admin-user-management-loading-state">
              <div className="admin-user-management-spinner"></div>
              <p>{t("adminUserManagement.loading")}</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="admin-user-management-empty-state">
              <div className="admin-user-management-empty-icon">
                <FaUsers aria-hidden />
              </div>
              <h3>{t("adminUserManagement.noUsers")}</h3>
              <p>{t("adminUserManagement.tryAdjusting")}</p>
            </div>
          ) : (
            <div className="admin-user-management-table-container">
              <table className="admin-user-management-data-table">
                <thead>
                  <tr>
                    <th>{t("adminUserManagement.user")}</th>
                    {!isFarmersPage && !isConsumersPage && (
                      <th>{t("adminUserManagement.role")}</th>
                    )}
                    <th>{t("adminUserManagement.email")}</th>
                    <th>{t("adminUserManagement.phone")}</th>
                    <th>{t("adminUserManagement.location")}</th>
                    <th>{t("adminUserManagement.sessionStatus")}</th>
                    {isFarmersPage && (
                      <th>{t("adminUserManagement.verified")}</th>
                    )}
                    <th>{t("adminUserManagement.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageUsers.map((userItem) => (
                    <tr key={userItem._id}>
                      <td className="user-cell">
                        <div className="admin-user-management-user-info">
                          <img
                            src={getProfileImage(userItem)}
                            alt={userItem.name}
                            className="admin-user-management-user-avatar"
                            referrerPolicy="no-referrer"
                          />
                          <div>
                            <div className="admin-user-management-user-name">
                              {userItem.name}
                            </div>
                            <div className="admin-user-management-user-id">
                              ID: {userItem._id?.slice(-8)}
                            </div>
                          </div>
                        </div>
                      </td>
                      {!isFarmersPage && !isConsumersPage && (
                        <td>
                          <span
                            className={`admin-user-management-role-badge ${userItem.role}`}
                          >
                            {userItem.role === "farmer" ? (
                              <>
                                <RoleIllustration role="farmer" size="sm" />{" "}
                                Farmer
                              </>
                            ) : (
                              <>
                                <RoleIllustration role="consumer" size="sm" />{" "}
                                Consumer
                              </>
                            )}
                          </span>
                        </td>
                      )}
                      <td>{userItem.email || "N/A"}</td>
                      <td>{userItem.phone || "N/A"}</td>
                      <td>
                        {userItem.address?.district || "N/A"},{" "}
                        {userItem.address?.province || ""}
                      </td>
                      <td>
                        <span
                          className={`admin-user-management-status-badge ${userItem.isOnline ? "active" : "inactive"}`}
                        >
                          {userItem.isOnline
                            ? t("adminUserManagement.online")
                            : t("adminUserManagement.inactiveSession")}
                        </span>
                      </td>
                      {isFarmersPage && (
                        <td>
                          {userItem.verified ? (
                            <span className="admin-user-management-verified-badge">
                              <FaCheck aria-hidden /> Verified
                            </span>
                          ) : (
                            <span className="admin-user-management-unverified-badge">
                              <FaTimes aria-hidden /> Not Verified
                            </span>
                          )}
                        </td>
                      )}
                      <td className="admin-user-management-actions-cell">
                        <div className="admin-user-management-action-buttons">
                          {userItem.role === "farmer" && !userItem.verified && (
                            <button
                              onClick={() => handleVerify(userItem._id)}
                              className="admin-user-management-action-btn approve"
                            >
                              {t("adminUserManagement.approve")}
                            </button>
                          )}
                          {userItem.role === "farmer" && (
                            <Link
                              to={`/users/${userItem.username || userItem._id}`}
                              className="admin-user-management-action-btn view"
                            >
                              {t("adminUserManagement.view")}
                            </Link>
                          )}
                          {userItem.verified && (
                            <button
                              onClick={() => handleBlock(userItem._id)}
                              className="admin-user-management-action-btn block"
                            >
                              {t("adminUserManagement.block")}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(userItem._id)}
                            className="admin-user-management-action-btn delete"
                          >
                            {t("adminUserManagement.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUserManagement;
